import type { PhaseFiveDto, PhasePlantSiteDto } from '@bk/contracts';
import { useState } from 'react';
import { useTenant } from '../../lib/useTenant';
import { Figures, Scrap, talkAbout } from './Fase';
import { Helps } from './Fase4';
import { PhaseFrame, usePhase } from './fasi-comune';
import type { PhasePageProps } from './fasi-pagine';

const SCRAP_INTRO = 'Gli impianti che l’azienda al suo meglio avrebbe in queste sedi per queste attività e che le fonti non mostrano, e i registri che mancano. Lo scarto dice cosa le fonti mostrano o non mostrano oggi, non se fate bene o male.';

/** Una sede con i suoi impianti nei tre gruppi (art. 3.101): grado e fonte, di chi è, registro (art. 3.102). */
function PlantSite({ s }: { s: PhasePlantSiteDto }) {
  const [why, setWhy] = useState(false);
  const { isManager } = useTenant();
  const weak = /scelta|confermare/i.test(s.titolo_uso_fonte) || /confermare/i.test(s.titolo_uso);
  return (
    <article className="sn-site">
      <div className="sn-ph">
        <h2 className="sn-h2">{s.nome}</h2>
        <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <span className="sn-tag">{s.titolo_uso}</span>
          <span className={`sn-src ${weak ? 'gold' : /T$/.test(s.titolo_uso_fonte) ? 'violet' : ''}`}>{s.titolo_uso_fonte}</span>
        </span>
      </div>
      {s.nota && <p className="sn-note">{s.nota}</p>}
      {s.gruppi.map((g) => (
        <div key={g.gruppo} style={{ display: 'grid', gap: 6 }}>
          <p className="lbl" style={{ margin: '6px 0 0' }}>
            {g.titolo.toUpperCase()}
          </p>
          {g.impianti.length ? (
            <table className="sn-tbl">
              <thead>
                <tr>
                  <th>Impianto</th>
                  <th style={{ width: 240 }}>Grado e fonte</th>
                  <th style={{ width: 130 }}>Di chi è</th>
                  <th>Registro: cosa, ogni quanto, chi</th>
                </tr>
              </thead>
              <tbody>
                {g.impianti.map((p) => (
                  <tr key={p.id}>
                    <td className="g">
                      {p.nome}
                      {p.quantita ? ` · ${p.quantita}` : ''}
                      {p.nota && <span className="sub">La tua risposta: {p.nota}</span>}
                    </td>
                    <td>
                      <span className={`sn-tag ${p.presenza === 'presente' && p.grado === 'certo' ? 'ok' : 'gold'}`}>{p.presenza === 'presente' ? `${p.grado} · presente` : 'probabile'}</span>
                      <span className="sub">{p.fonte}</span>
                    </td>
                    <td className="v">{p.di_chi === 'azienda' ? 'dell’azienda' : 'del proprietario'}</td>
                    <td className="f">{p.registro ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="sn-note">Nessuno in questa sede.</p>
          )}
        </div>
      ))}
      <div className="sn-pills">
        <span className="lbl">
          {s.conto.totale} {s.conto.totale === 1 ? 'IMPIANTO' : 'IMPIANTI'} · EDIFICIO {s.conto.per_gruppo.impianti_edificio} · ATTREZZATURE {s.conto.per_gruppo.attrezzature} · INFORMATICA {s.conto.per_gruppo.dotazioni_informatiche}
        </span>
        <span className="sn-tag">Responsabile: {s.responsabile.toLowerCase()}</span>
      </div>
      <div className="acts">
        <button type="button" className="sn-link" style={{ fontSize: 14, fontWeight: 600 }} onClick={() => setWhy(!why)}>
          Perché? Le fonti di questa sede
        </button>
        {isManager && (
          <>
            <button type="button" className="sn-link" style={{ color: '#c3cbe0' }} onClick={() => talkAbout(`Sugli impianti della sede ${s.nome}: `)}>
              Correggi
            </button>
            <button type="button" className="sn-link" style={{ color: '#c3cbe0' }} onClick={() => talkAbout(`Nella sede ${s.nome} abbiamo anche: `)}>
              Aggiungi un impianto
            </button>
          </>
        )}
      </div>
      {why && (
        <p className="sn-why" style={{ fontSize: 13 }}>
          <span className="q">Le fonti di questa sede.</span> Titolo d’uso: {s.titolo_uso.toLowerCase()} ({s.titolo_uso_fonte}).{' '}
          {s.gruppi
            .flatMap((g) => g.impianti)
            .map((p) => `${p.nome}: ${p.fonte}`)
            .join('; ')}
          .
        </p>
      )}
    </article>
  );
}

/**
 * La fase 5 «Gli impianti» (docs/mockup/percorso-sinapsi/08-fase5; contratto n. 1 versione 2.4, art. 8, fase 5; ADR
 * 0176): con cosa lavora l'azienda in ogni sede, di chi è e cosa richiede per restare in efficienza. Nessuna norma: la
 * scrive la fase 6. Nessun programma: è della fase 7.
 */
export function Fase5Page({ next, onNext, sealing, stopped = false }: PhasePageProps) {
  const { q, reload, confirm, redo } = usePhase<PhaseFiveDto>(5);
  return (
    <PhaseFrame
      // Lo scarto va prima delle domande, come nella pagina di prova: lo mostra `beforeQuestions`, non la cornice.
      d={q.data && { ...q.data, scarto: [] }}
      error={q.error}
      eyebrow="Fase 5 · Gli impianti"
      title={(d) => `Con cosa lavora ${d.azienda ?? 'la tua azienda'}, sede per sede`}
      working="Preparo gli impianti"
      questionsIntro="Solo gli impianti probabili che cambiano un obbligo con sanzione; gli altri restano «probabili»."
      questionsEmpty="Nessuna domanda per te in questa fase: nessun impianto probabile cambia un obbligo con sanzione."
      scrapIntro={SCRAP_INTRO}
      next={next}
      onNext={onNext}
      sealing={sealing}
      stopped={stopped}
      onConfirm={confirm}
      onRedo={redo}
      reload={reload}
      beforeQuestions={(d) => (
        <>
          <Scrap list={q.data?.scarto ?? []} intro={SCRAP_INTRO} />
          <Helps list={d.aiuterebbero} />
        </>
      )}
    >
      {(d) => (
        <>
          <Figures list={d.numeri} />
          {d.sedi.map((s) => (
            <PlantSite key={s.id} s={s} />
          ))}
          <p className="sn-note">Cosa chiede la legge per ognuno di questi impianti lo scrive la fase 6, che li legge tutti. I programmi non sono qui: sono nella fase 7.</p>
        </>
      )}
    </PhaseFrame>
  );
}
